import { S25Const } from "../util/s25-const";
import { S25Util } from "../util/s25-util";
import { computed, signal } from "@angular/core";

export enum NetworkHealthStatus {
    Healthy = "healthy",
    Slow = "slow",
    Degraded = "degraded",
    Offline = "offline",
}

interface RequestTiming {
    durationMs: number;
    timestamp: number;
}

interface NetworkConnection extends EventTarget {
    effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
    downlink?: number;
    rtt?: number; // round-trip time in milliseconds — the estimated time for a packet to travel to the server and back, as reported by the Network Information API https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation/rtt
    saveData?: boolean;
}

const SLOW_THRESHOLD_MS = 5000;
const DEGRADED_THRESHOLD_MS = 12000;
const TIMING_WINDOW_MS = 2 * S25Const.ms.min;
const MIN_SAMPLES_FOR_SLOW = 3;
const SLOW_RATIO_THRESHOLD = 0.5;
const PING_INTERVAL_HEALTHY_MS = 30 * S25Const.ms.sec;
const PING_INTERVAL_UNHEALTHY_MS = 10 * S25Const.ms.sec;
const PING_TIMEOUT_MS = 10 * S25Const.ms.sec;
const DISMISS_COOLDOWN_MS = 5 * S25Const.ms.min;

export const STATUS_MESSAGES: Record<NetworkHealthStatus, string | null> = {
    [NetworkHealthStatus.Offline]: "You appear to be offline. Check your connection and try again.",
    [NetworkHealthStatus.Degraded]: "Your network connection is very slow. Some features may not work as expected.",
    [NetworkHealthStatus.Slow]: "Your network connection seems slow. Loading may take longer than usual.",
    [NetworkHealthStatus.Healthy]: null,
};

const STATUS_SEVERITY: NetworkHealthStatus[] = [
    NetworkHealthStatus.Offline,
    NetworkHealthStatus.Degraded,
    NetworkHealthStatus.Slow,
    NetworkHealthStatus.Healthy,
];

export class NetworkHealthService {
    static status = signal<NetworkHealthStatus>(NetworkHealthStatus.Healthy);
    static healthy = computed(() => this.status() === NetworkHealthStatus.Healthy);
    private static recentTimings: RequestTiming[] = [];
    private static listeners: Set<(status: NetworkHealthStatus) => void> = new Set();
    private static pingIntervalId: ReturnType<typeof setInterval> | null = null;
    private static started = false;
    private static userDismissedAt: number | null = null;
    private static bannerEl: HTMLElement | null = null;

    static start() {
        if (!S25Const.inApp || this.started) return;
        this.started = true;

        window.addEventListener("offline", () => this.updateStatus(NetworkHealthStatus.Offline));
        window.addEventListener("online", () => this.evaluate());

        const conn = this.getConnection();
        conn?.addEventListener("change", () => this.evaluateConnectionApi());

        if (!navigator.onLine) {
            this.updateStatus(NetworkHealthStatus.Offline);
        } else {
            this.evaluateConnectionApi();
        }

        this.injectBannerStyles();
        this.startPingLoop();
    }

    static redisplayBanner() {
        if (!this.isDismissed()) return;
        this.userDismissedAt = null;
        this.renderBanner();
    }

    static getStatusMessage(): string | null {
        return STATUS_MESSAGES[this.status()];
    }

    static isDismissed(): boolean {
        if (!this.userDismissedAt) return false;
        return Date.now() - this.userDismissedAt < DISMISS_COOLDOWN_MS;
    }

    static dismiss() {
        this.userDismissedAt = Date.now();
        this.renderBanner();
    }

    static onStatusChange(listener: (status: NetworkHealthStatus) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    static recordRequestTiming(durationMs: number) {
        if (!this.started) return;
        const now = Date.now();
        this.recentTimings.push({ durationMs, timestamp: now });
        this.pruneTimings(now);
        this.evaluate();
    }

    private static pruneTimings(now: number) {
        this.recentTimings = this.recentTimings.filter((t) => now - t.timestamp < TIMING_WINDOW_MS);
    }

    private static evaluate() {
        if (!navigator.onLine) {
            this.updateStatus(NetworkHealthStatus.Offline);
            return;
        }

        const connIdx = STATUS_SEVERITY.indexOf(this.getConnectionApiStatus());
        const timingIdx = STATUS_SEVERITY.indexOf(this.getTimingStatus());
        this.updateStatus(STATUS_SEVERITY[Math.min(connIdx, timingIdx)]);
    }

    private static evaluateConnectionApi() {
        if (!navigator.onLine) {
            this.updateStatus(NetworkHealthStatus.Offline);
            return;
        }
        const connStatus = this.getConnectionApiStatus();
        if (connStatus !== NetworkHealthStatus.Healthy) {
            this.updateStatus(connStatus);
        } else {
            this.evaluate();
        }
    }

    private static getConnectionApiStatus(): NetworkHealthStatus {
        const conn = this.getConnection();
        if (!conn) return NetworkHealthStatus.Healthy;

        if (conn.effectiveType === "slow-2g" || conn.effectiveType === "2g") {
            return NetworkHealthStatus.Degraded;
        }
        if (conn.effectiveType === "3g" || (S25Util.isDefined(conn.rtt) && conn.rtt > 1000)) {
            return NetworkHealthStatus.Slow;
        }

        return NetworkHealthStatus.Healthy;
    }

    private static getTimingStatus(): NetworkHealthStatus {
        if (this.recentTimings.length < MIN_SAMPLES_FOR_SLOW) return NetworkHealthStatus.Healthy;

        const degradedCount = this.recentTimings.filter((t) => t.durationMs >= DEGRADED_THRESHOLD_MS).length;
        if (degradedCount / this.recentTimings.length >= SLOW_RATIO_THRESHOLD) {
            return NetworkHealthStatus.Degraded;
        }

        const slowCount = this.recentTimings.filter((t) => t.durationMs >= SLOW_THRESHOLD_MS).length;
        if (slowCount / this.recentTimings.length >= SLOW_RATIO_THRESHOLD) {
            return NetworkHealthStatus.Slow;
        }

        return NetworkHealthStatus.Healthy;
    }

    private static updateStatus(newStatus: NetworkHealthStatus) {
        if (this.status() === newStatus) return;

        if (newStatus === NetworkHealthStatus.Healthy) {
            this.userDismissedAt = null;
        }

        this.status.set(newStatus);
        this.notifyListeners();
        this.adjustPingInterval();
        this.renderBanner();
    }

    private static notifyListeners() {
        for (const listener of this.listeners) {
            try {
                listener(this.status());
            } catch (e) {}
        }
    }

    private static getConnection(): NetworkConnection | null {
        return (
            (navigator as any).connection ??
            (navigator as any).mozConnection ??
            (navigator as any).webkitConnection ??
            null
        );
    }

    private static startPingLoop() {
        this.schedulePing(PING_INTERVAL_HEALTHY_MS);
    }

    private static adjustPingInterval() {
        const interval =
            this.status() === NetworkHealthStatus.Healthy ? PING_INTERVAL_HEALTHY_MS : PING_INTERVAL_UNHEALTHY_MS;
        this.schedulePing(interval);
    }

    private static schedulePing(intervalMs: number) {
        if (this.pingIntervalId != null) clearInterval(this.pingIntervalId);
        this.pingIntervalId = setInterval(() => this.ping(), intervalMs);
    }

    private static async ping() {
        if (!S25Const.webservicesBaseUrl) return;

        const url = S25Const.webservicesBaseUrl + "/ping.json?caller=pro-network-health&_=" + Date.now();
        const start = performance.now();

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

            await fetch(url, {
                method: "HEAD",
                signal: controller.signal,
                cache: "no-store",
            });

            clearTimeout(timeoutId);
            this.recordRequestTiming(performance.now() - start);
        } catch {
            if (!navigator.onLine) {
                this.updateStatus(NetworkHealthStatus.Offline);
            } else {
                this.recordRequestTiming(PING_TIMEOUT_MS);
            }
        }
    }

    private static injectBannerStyles() {
        const style = document.createElement("style");
        style.textContent = `
            .s25-network-banner {
                position: fixed;
                top: env(safe-area-inset-top);
                left: 0;
                right: 0;
                z-index: 10000;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 1em;
                font-size: 1rem;
                font-weight: 500;
                transition: transform 0.3s ease, opacity 0.3s ease;
                box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            }
            .s25-network-banner--hidden {
                transform: translateY(-100%);
                opacity: 0;
                pointer-events: none;
            }
            .s25-network-banner--slow {
                background: #fff3cd;
                color: #664d03;
                border-bottom: 2px solid #ffc107;
            }
            .s25-network-banner--degraded {
                background: #ffe0cc;
                color: #7a3300;
                border-bottom: 2px solid #fd7e14;
            }
            .s25-network-banner--offline {
                background: #f8d7da;
                color: #842029;
                border-bottom: 2px solid #dc3545;
            }
            .s25-network-banner__message {
                flex: 1;
                margin-right: 0.75em;
            }
            .s25-network-banner__dismiss {
                background: none;
                border: none;
                font-size: 24px;
                cursor: pointer;
                color: inherit;
                opacity: 0.7;
                padding: 0 0.25em;
                line-height: 1;
            }
            .s25-network-banner__dismiss:hover {
                opacity: 1;
            }
            body.s25-network-banner-visible {
                padding-top: 36px;
            }
        `;
        document.head.appendChild(style);
    }

    private static renderBanner() {
        const message = STATUS_MESSAGES[this.status()];
        const shouldShow = !!message && !this.isDismissed();

        if (!shouldShow) {
            if (this.bannerEl) {
                this.bannerEl.classList.add("s25-network-banner--hidden");
                document.body.classList.remove("s25-network-banner-visible");
            }
            return;
        }

        if (!this.bannerEl) {
            this.bannerEl = document.createElement("div");
            this.bannerEl.className = "s25-network-banner s25-network-banner--hidden";
            this.bannerEl.setAttribute("role", "alert");
            this.bannerEl.innerHTML = `
                <span class="s25-network-banner__message"></span>
                <button class="s25-network-banner__dismiss" aria-label="Dismiss">&times;</button>
            `;
            this.bannerEl
                .querySelector(".s25-network-banner__dismiss")!
                .addEventListener("click", () => this.dismiss());
            document.body.prepend(this.bannerEl);
        }

        this.bannerEl.querySelector(".s25-network-banner__message")!.textContent = message;

        this.bannerEl.classList.remove(
            "s25-network-banner--slow",
            "s25-network-banner--degraded",
            "s25-network-banner--offline",
        );
        this.bannerEl.classList.add(`s25-network-banner--${this.status()}`);

        requestAnimationFrame(() => {
            this.bannerEl!.classList.remove("s25-network-banner--hidden");
            document.body.classList.add("s25-network-banner-visible");
        });
    }
}
